import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { usersAPI } from '../../api';
import { apiError, joinWords } from './usersUtils';

/**
 * Delete a person, but only one with no records at all (a test account, someone added
 * by mistake). For anyone with sales or other records it explains why not and offers
 * to switch them off instead. The server checks again when deleting.
 */
const DeletePersonModal = ({ isOpen, onClose, person, onDeleted, onSwitchOff }) => {
  const [check, setCheck] = useState(null); // { can_delete, records, reason }
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen || !person) return undefined;
    let cancelled = false;
    setCheck(null);
    setProblem('');
    usersAPI
      .records(person.id)
      .then((response) => !cancelled && setCheck(response.data))
      .catch((error) => !cancelled && setProblem(apiError(error, 'Could not check their records. Please try again.')));
    return () => {
      cancelled = true;
    };
  }, [isOpen, person]);

  if (!person) return null;
  const name = person.full_name || person.username;

  const handleDelete = async () => {
    setBusy(true);
    try {
      await usersAPI.remove(person.id);
      onDeleted(person);
    } catch (error) {
      setProblem(apiError(error, 'Could not delete. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  const hasRecords = check && check.records.length > 0;
  const title = !check ? `Delete ${name}?` : check.can_delete ? `Delete ${name}?` : `${name} can't be deleted`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className="space-y-4">
        {problem && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {problem}
          </div>
        )}

        {!check && !problem && (
          <div className="flex items-center gap-3 text-sm text-gray-600">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            Checking their records…
          </div>
        )}

        {check?.reason && <p className="text-sm text-gray-700">{check.reason}</p>}

        {check && !check.reason && hasRecords && (
          <>
            <p className="text-sm text-gray-700">
              {name} has {joinWords(check.records)} on record. Old records keep their name.
            </p>
            <p className="text-sm text-gray-700">
              {person.is_active
                ? "Switch them off instead. They won't be able to sign in."
                : "They are already switched off and can't sign in."}
            </p>
          </>
        )}

        {check?.can_delete && (
          <>
            <p className="text-sm text-gray-700">They have no sales or other records.</p>
            <p className="text-sm text-gray-700">This can&apos;t be undone. You can add them again later.</p>
          </>
        )}

        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} autoFocus>
            {check?.can_delete ? 'Cancel' : 'Close'}
          </Button>
          {check?.can_delete && (
            <Button variant="danger" onClick={handleDelete} disabled={busy}>
              {busy ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          {check && !check.reason && hasRecords && person.is_active && (
            <Button variant="danger" onClick={() => onSwitchOff(person)}>
              Switch off
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DeletePersonModal;
